import { z } from "zod";
import {CursorPaginationRequestSchema,CursorPaginationResponseSchema,} from "./pagination";
import { ApiResponseSuccessSchema } from "./common";
// import { CreateCountryRequestSchema } from "./country";
import { VenueWithoutTimestampsSchema } from "./venue";

// ======================
// QUERY SCHEMA
// ======================
export const FixtureWithPredictionQuerySchema =
  CursorPaginationRequestSchema.extend({
    poolId: z.string(),
    viewUserId: z.string().optional(),
    liveOnly: z.preprocess((value) => {
      if (typeof value === "boolean") return value;
      if (typeof value === "string") {
        if (value.toLowerCase() === "true") return true;
        if (value.toLowerCase() === "false") return false;
      }
      return value;
    }, z.boolean().optional()),
    scoredOnly: z.preprocess((value) => {
      if (typeof value === "boolean") return value;
      if (typeof value === "string") {
        if (value.toLowerCase() === "true") return true;
        if (value.toLowerCase() === "false") return false;
      }
      return value;
    }, z.boolean().optional()),
  });
export type FixtureWithPredictionQueryDTO = z.infer<typeof FixtureWithPredictionQuerySchema>;

export const FixtureCountrySchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
  flag: z.string().nullable().optional(),
});
export type FixtureCountryDTO = z.infer<typeof FixtureCountrySchema>;

// ======================
// TEAM SCHEMA
// ======================
export const FixtureTeamWithCountrySchema = z.object({
  homeParticipantId: z.string().optional(),
  awayParticipantId: z.string().optional(),
  id: z.string(),
  name: z.string(),
  code: z.string().nullable().optional(),
  founded: z.number().nullable().optional(),
  national: z.boolean(),
  logo: z.string().nullable().optional(),
  // country: CreateCountryRequestSchema,
  country: FixtureCountrySchema,
});
export type FixtureTeamWithCountryDTO = z.infer<typeof FixtureTeamWithCountrySchema>;

// ======================
// STATUS SCHEMA
// ======================
export const FixtureStatusSchema = z.object({
  id: z.string(),
  name: z.string(),
  shortName: z.string().nullable().optional(),
  isInProgress: z.boolean(),
  isFinished: z.boolean(),
});
export type FixtureStatusDTO = z.infer<typeof FixtureStatusSchema>;

// ======================
// GROUP SCHEMA
// ======================
export const FixtureGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type FixtureGroupDTO = z.infer<typeof FixtureGroupSchema>;

// ======================
// REAL RESULT SCHEMA
// ======================
export const FixtureRealResultSchema = z.object({
  home: z.number(),
  away: z.number(),
  home_penalties: z.number().nullable(),
  away_penalties: z.number().nullable(),
  winner: z.enum(["HOME", "AWAY", "DRAW"]).nullable(),
});
export type FixtureRealResultDTO = z.infer<typeof FixtureRealResultSchema>;

export const FixtureMatchScoreSchema = z.object({
  home: z.number(),
  away: z.number(),
});
export type FixtureMatchScoreDTO = z.infer<typeof FixtureMatchScoreSchema>;

// ======================
// PREDICTION SCHEMA
// ======================
export const FixturePredictionSchema = z.object({
  home: z.number(),
  away: z.number(),
  winner: z.enum(["HOME", "AWAY", "DRAW"]).nullable().optional(),
});
export type FixturePredictionDTO = z.infer<typeof FixturePredictionSchema>;

// ======================
// SCORING SCHEMA
// ======================
export const FixtureScoringBreakdownSchema = z.object({
  key: z.string(),
  points: z.number(),
});
export type FixtureScoringBreakdownDTO = z.infer<typeof FixtureScoringBreakdownSchema>;

export const FixtureScoringSchema = z.object({
  totalPoints: z.number(),
  breakdown: z.array(FixtureScoringBreakdownSchema),
});
export type FixtureScoringDTO = z.infer<typeof FixtureScoringSchema>;

// ======================
// USER PREDICTION SCHEMA
// ======================
export const FixtureUserPredictionSchema = z.object({
  hasPredicted: z.boolean(),
  isEditable: z.boolean(),
  isMissed: z.boolean(),
  isLockedByAdmin: z.boolean().optional(),
  prediction: FixturePredictionSchema.nullable().optional(),
  scoring: FixtureScoringSchema.nullable().optional(),
});
export type FixtureUserPredictionDTO = z.infer<typeof FixtureUserPredictionSchema>;

// ======================
// FIXTURE WITH PREDICTION RESPONSE
// ======================
export const FixtureWithPredictionResponseSchema = z.object({
  seasonId: z.string(),
  matchId: z.string(),
  homeTeam: FixtureTeamWithCountrySchema,
  awayTeam: FixtureTeamWithCountrySchema,
  date: z.string().datetime().nullable(),
  status: FixtureStatusSchema,
  matchType: z.enum(["regular", "knockout"]),
  venue: VenueWithoutTimestampsSchema.nullable().optional(),
  group: FixtureGroupSchema.nullable().optional(),
  matchScore: FixtureMatchScoreSchema.nullable().optional(),
  realResult: FixtureRealResultSchema.nullable().optional(),
  userPrediction: FixtureUserPredictionSchema,
});
export type FixtureWithPredictionResponseDTO = z.infer<typeof FixtureWithPredictionResponseSchema>;

// ======================
// PAGINATED RESPONSE
// ======================
export const FixturesWithPredictionsPaginatedResponseSchema =
  CursorPaginationResponseSchema(FixtureWithPredictionResponseSchema);

export type FixturesWithPredictionsPaginatedResponseDTO = z.infer<
  typeof FixturesWithPredictionsPaginatedResponseSchema
>;

// ======================
// API RESPONSE WRAPPER
// ======================
export const FixturesWithPredictionsResponseSchema =
  ApiResponseSuccessSchema(FixturesWithPredictionsPaginatedResponseSchema);
export type FixturesWithPredictionsResponseDTO = z.infer<
  typeof FixturesWithPredictionsResponseSchema
>;
